<template>
    <div class="columns is-multiline">
        <div class="column is-12">
            <div class="buttons">
                <button class="button is-success" @click="openSelectCalculationRulesModal">
                    <span class="icon">
                        <i class="fas fa-plus"></i>
                    </span>
                    <span>{{ translate("event_ranking.add_rule") }}</span>
                </button>

                <button :disabled="!canBeRefreshed" class="button" @click="refreshRanking">
                    <span class="icon">
                        <i class="fas fa-sync"></i>
                    </span>
                    <span>{{ translate("event_ranking.refresh_ranking") }}</span>
                </button>

                <button class="button is-primary" @click="beginSaveMode" :disabled="saveMode">
                    <span class="icon">
                        <i class="fas fa-check"></i>
                    </span>
                    <span>{{ translate("event_ranking.save_ranking") }}</span>
                </button>

                <div v-if="saveMode" class="is-flex is-align-items-center">
                    <input
                        class="input mb-2 mr-2"
                        type="text"
                        :placeholder="translate('event_ranking.enter_name_for_ranking')"
                        v-model="nameForRanking"
                    />

                    <LoadingButton :is-loading="savingInProgress" class="button is-success" @click="saveRanking" :disabled="!canBeSaved">
                        {{ translate("common.ok") }}
                    </LoadingButton>
                    <button class="button is-danger" @click="cancelSaveMode">{{ translate("common.cancel") }}</button>
                </div>
            </div>
        </div>

        <div class="column is-12">
            <p class="mb-3">{{ translate("page.admin_academy_results.your_rules") }}</p>

            <div class="columns" v-if="store.definedRules.length > 0">
                <div class="column is-narrow" v-for="(rule, index) in store.definedRules" :key="rule.rule">
                    <div class="box is-flex is-align-items-center">
                        <AwardPointsForWinningMatchRuleView
                            v-if="rule.rule === 'awardPointsForWin'"
                            :rule="rule.rule"
                            :rule-arguments="rule.arguments"
                        />

                        <button class="ml-3 button is-small is-danger" @click="removeRule(index)">
                            <span class="icon">
                                <i class="fas fa-times"></i>
                            </span>
                        </button>
                    </div>
                </div>
            </div>
            <div v-else class="has-text-centered is-size-5">
                <p>{{ translate("event_ranking.no_rules_defined") }}</p>
            </div>
        </div>
    </div>

    <hr />

    <div class="columns">
        <div class="column">
            <div class="is-flex is-justify-content-center" v-if="generatingInProgress">
                <LoadingSpinner2 />
            </div>
            <GeneratedRankingTable v-else-if="store.generatedRanking.competitors.length > 0" :generated-ranking="store.generatedRanking" />
            <div v-else>
                <Notification class="is-info has-text-centered is-size-5">
                    {{ translate("event_ranking.no_ranking_generated_yet") }}
                </Notification>
            </div>
        </div>
    </div>

    <SelectCalculationRulesModal ref="selectCalculationRulesModal" />
</template>

<script setup lang="ts">
    import { computed, onMounted, ref } from "vue";
    import SelectCalculationRulesModal from "./CalculationRules/SelectCalculationRulesModal.vue";
    import { useEventRankingsStore } from "./eventRankingsStore";
    import LoadingSpinner2 from "../Common/LoadingSpinner2.vue";
    import { translate } from "../Common/translations";
    import Notification from "../Common/Notification.vue";
    import eventRankingsApi from "../Api/eventRankings";
    import { handleError } from "../Common/errorHandler";
    import LoadingButton from "../Common/LoadingButton.vue";
    import { successToast } from "../Common/toasts";
    import GeneratedRankingTable from "./GeneratedRankingTable.vue";
    import AwardPointsForWinningMatchRuleView from "./ViewingDefinedRules/AwardPointsForWinningMatchRuleView.vue";

    const store = useEventRankingsStore();

    // CALC RULES MODAL

    const selectCalculationRulesModal = ref<InstanceType<typeof SelectCalculationRulesModal> | null>(null);

    function openSelectCalculationRulesModal() {
        selectCalculationRulesModal.value?.open();
    }

    // REMOVE RULES
    function removeRule(index: number) {
        store.definedRules.splice(index, 1);
        store.persistInLocalStorage();
        return store.refreshRanking();
    }

    // REFRESHING RANKING

    const canBeRefreshed = computed(() => {
        return store.definedRules.length > 0;
    });

    const generatingInProgress = ref<boolean>(false);

    function refreshRanking() {
        generatingInProgress.value = true;

        return store
            .refreshRanking()
            .catch(handleError)
            .finally(() => {
                generatingInProgress.value = false;
            });
    }

    // SAVING RANKING
    const nameForRanking = ref("");
    const saveMode = ref(false);
    const canBeSaved = computed(() => {
        return canBeRefreshed.value && nameForRanking.value.length > 0;
    });
    const savingInProgress = ref(false);

    function beginSaveMode() {
        saveMode.value = true;
    }

    function cancelSaveMode() {
        saveMode.value = false;
    }

    function saveRanking() {
        if (!canBeSaved.value) {
            return;
        }
        savingInProgress.value = true;
        return eventRankingsApi
            .saveRanking({
                eventId: store.eventId,
                name: nameForRanking.value,
                rules: {
                    ruleDefinitions: store.definedRules,
                },
            })
            .then(() => {
                successToast(translate("event_ranking.ranking_saved_succ"));
                store.definedRules = [];
                store.generatedRanking = { competitors: [] };
                store.persistInLocalStorage();
                nameForRanking.value = "";
            })
            .catch(handleError)
            .finally(() => {
                savingInProgress.value = false;
                saveMode.value = false;
            });
    }

    //LOADING
    onMounted(() => {
        store.loadFromLocalStorage();
        return refreshRanking();
    });
</script>

<style scoped></style>
